import { Billplan, FiscalOffer, FiscalQuota, Plan } from '@common/models';

import { extraUsersFrequencyFactor, extraUsersMonthsForFrequency } from './extra-users.util';

/**
 * Cupo fiscal extra (ADM-740): facturas mensuales por encima del cupo base del Plan
 * Fiscal. Espejo de api-admin (`shared/subscription-period-amount.helper.ts`,
 * `shared/fiscal-quota.helper.ts`, `constants/fiscalSales.ts`) y de api
 * (`constants/fiscalSales.ts`). Si cambia una regla allá, cambia aquí en el mismo ciclo.
 */

/** Tope de servidor de `extraControlNumbers` (`MAX_EXTRA_CONTROL_NUMBERS` de api-admin). */
export const MAX_EXTRA_CONTROL_NUMBERS = 100000;

/** Claves de cargo que la factura sella en `specificData`. */
export const EXTRA_CONTROL_NUMBERS_KEYS = {
    count: 'extraControlNumbersCount',
    monthlyUSD: 'extraControlNumbersMonthlyUSD',
    baseUSD: 'extraControlNumbersBaseUSD',
} as const;

const FISCAL_QUOTA_IVA_RATE = 0.16;

/** `errorCode` de los 409 de api-admin; viajan en la RAÍZ del body (`publicErrorCodeField`). */
export const FISCAL_SALES_DISABLED = 'FISCAL_SALES_DISABLED';
export const COUPON_FISCAL_QUOTA_CHANGE_UNSUPPORTED = 'COUPON_FISCAL_QUOTA_CHANGE_UNSUPPORTED';
export const MID_CYCLE_FISCAL_QUOTA_CHANGE_UNSUPPORTED = 'MID_CYCLE_FISCAL_QUOTA_CHANGE_UNSUPPORTED';
export const FIRST_PAYMENT_FISCAL_QUOTA_CHANGE_UNSUPPORTED = 'FIRST_PAYMENT_FISCAL_QUOTA_CHANGE_UNSUPPORTED';

/**
 * Código propio del front (api-admin no lo emite): un 400 que rechaza la cantidad
 * pedida. Lo clasifica `fiscalQuotaIssue` por `title` porque esos 400 no traen `errorCode`.
 */
export const FISCAL_QUOTA_REJECTED = 'FISCAL_QUOTA_REJECTED';

export type FiscalQuotaConflictCode =
    | typeof FISCAL_SALES_DISABLED
    | typeof COUPON_FISCAL_QUOTA_CHANGE_UNSUPPORTED
    | typeof MID_CYCLE_FISCAL_QUOTA_CHANGE_UNSUPPORTED
    | typeof FIRST_PAYMENT_FISCAL_QUOTA_CHANGE_UNSUPPORTED;

export type FiscalQuotaIssueCode = FiscalQuotaConflictCode | typeof FISCAL_QUOTA_REJECTED;

const FISCAL_QUOTA_CONFLICT_CODES: readonly string[] = [
    FISCAL_SALES_DISABLED,
    COUPON_FISCAL_QUOTA_CHANGE_UNSUPPORTED,
    MID_CYCLE_FISCAL_QUOTA_CHANGE_UNSUPPORTED,
    FIRST_PAYMENT_FISCAL_QUOTA_CHANGE_UNSUPPORTED,
];

/**
 * Títulos de los 400 EXCLUSIVOS del cupo fiscal (`EXTRA_CONTROL_NUMBERS_NOT_AVAILABLE` y
 * `EXTRA_CONTROL_NUMBERS_REQUIRES_FISCAL_PLAN` de api-admin). «Cantidad inválida» queda
 * fuera a propósito: api-admin usa ese mismo título para el 400 de `pendingPurchasedUsers`
 * (ADM-743) y para el de `controlNumbers` del mix, así que no identifica al cupo fiscal.
 */
export const FISCAL_QUOTA_REJECTED_TITLES: readonly string[] = ['Cantidad no disponible', 'Plan sin facturación fiscal'];

/**
 * Clasifica el error de un cobro del plan:
 * - 409 con uno de los cuatro `errorCode` en la raíz del body → ese código;
 * - 400 con un título exclusivo del cupo fiscal, solo si la petición llevó
 *   `extraControlNumbers` (`intentSent`) → `FISCAL_QUOTA_REJECTED` («Plan sin facturación
 *   fiscal» también existe como 409 del mix: por eso se exige el 400);
 * - cualquier otro → `null` (lo maneja el flujo de siempre).
 */
export function fiscalQuotaIssue(err: any, { intentSent }: { intentSent: boolean }): FiscalQuotaIssueCode | null {
    const body = err?.error;
    if (err?.status === 409) {
        const code = body?.errorCode;
        return typeof code === 'string' && FISCAL_QUOTA_CONFLICT_CODES.includes(code) ? (code as FiscalQuotaConflictCode) : null;
    }
    if (err?.status === 400 && intentSent === true && FISCAL_QUOTA_REJECTED_TITLES.includes(body?.title)) {
        return FISCAL_QUOTA_REJECTED;
    }
    return null;
}

/**
 * Predicado de plan fiscal de api (`isFiscalPlan` / `FISCAL_PLAN_FILTER`):
 * `permissions.FISCAL_INVOICE` definido y distinto de `[]` (`null` cuenta). No mira `type`.
 * Pieza interna de `isFiscalOfferPlan`: el modal no la llama directo.
 */
export function planHasFiscalInvoice(plan: Pick<Plan, 'permissions'> | null | undefined): boolean {
    const value = (plan?.permissions as { FISCAL_INVOICE?: unknown } | null | undefined)?.FISCAL_INVOICE;
    if (value === undefined) {
        return false;
    }
    return !(Array.isArray(value) && value.length === 0);
}

/** ÚNICA respuesta a «¿es el plan fiscal?» del pago de plan: permisos o el `planId` de la oferta. */
export function isFiscalOfferPlan(plan: Pick<Plan, '_id' | 'permissions'> | null | undefined, offer: FiscalOffer | null | undefined): boolean {
    if (!plan) {
        return false;
    }
    return planHasFiscalInvoice(plan) || (!!offer?.planId && String(plan._id) === offer.planId);
}

export const CLOSED_FISCAL_OFFER: Readonly<FiscalOffer> = Object.freeze({ available: false, planId: null, planType: null, includedQuantity: null, tiers: [] });

/**
 * Lectura ÚNICA de `bundle.fiscalOffer`, falla cerrado (mismo criterio que signup):
 * `available` solo con `true` estricto y un `planId`; un tier inválido vacía el catálogo.
 * Un tier de $0 es válido (`validateExtraControlNumberTiers` de api).
 */
export function normalizeFiscalOffer(raw: unknown): FiscalOffer {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
        return { ...CLOSED_FISCAL_OFFER, tiers: [] };
    }
    const offer = raw as Record<string, unknown>;
    const planId = typeof offer.planId === 'string' && offer.planId.trim() ? offer.planId.trim() : null;
    const planType = typeof offer.planType === 'string' && offer.planType.trim() ? offer.planType.trim() : null;
    const included = offer.includedQuantity;
    const includedQuantity = typeof included === 'number' && Number.isInteger(included) && included >= 0 ? included : null;
    return {
        available: offer.available === true && planId !== null,
        planId,
        planType,
        includedQuantity,
        tiers: normalizeFiscalTiers(offer.tiers),
    };
}

function normalizeFiscalTiers(raw: unknown): FiscalOffer['tiers'] {
    if (!Array.isArray(raw)) {
        return [];
    }
    const tiers: FiscalOffer['tiers'] = [];
    const seen = new Set<number>();
    for (const tier of raw) {
        const quantity = tier?.quantity;
        const amount = tier?.amount;
        const validQuantity = typeof quantity === 'number' && Number.isInteger(quantity) && quantity > 0 && !seen.has(quantity);
        const validAmount = typeof amount === 'number' && Number.isFinite(amount) && amount >= 0;
        if (!validQuantity || !validAmount) {
            return [];
        }
        seen.add(quantity);
        tiers.push({ quantity, amount });
    }
    return tiers.sort((a, b) => a.quantity - b.quantity);
}

/**
 * Normalización de un cupo (del Tenant, de la factura o de un pedido), espejo de
 * `normalizeFiscalQuota` de api-admin: `null` sin `quantity` numérico; cantidad entera en
 * `[0, MAX]`; cantidad 0 ⇒ `{ 0, 0 }`; precio finito > 0 o 0, sin redondear.
 */
export function normalizeFiscalQuota(raw: unknown): FiscalQuota | null {
    if (raw === null || raw === undefined || typeof raw !== 'object' || Array.isArray(raw)) {
        return null;
    }
    const rawQuantity = Number((raw as { quantity?: unknown }).quantity);
    if (!Number.isFinite(rawQuantity)) {
        return null;
    }
    const quantity = Math.min(MAX_EXTRA_CONTROL_NUMBERS, Math.max(0, Math.floor(rawQuantity)));
    if (quantity === 0) {
        return { quantity: 0, monthlyAmountUSD: 0 };
    }
    const monthly = Number((raw as { monthlyAmountUSD?: unknown }).monthlyAmountUSD);
    return { quantity, monthlyAmountUSD: Number.isFinite(monthly) && monthly > 0 ? monthly : 0 };
}

function readSpecificNumber(specificData: unknown, key: string): number | null {
    if (!Array.isArray(specificData)) {
        return null;
    }
    const value = specificData.find(entry => entry?.key === key)?.valueNumber;
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Lo que la factura COBRA del extra (`readBilledFiscalQuota` de api-admin), estricto:
 * sin `extraControlNumbersCount` ⇒ `null` (la factura no cobra el extra); con 0 ⇒ `{ 0, 0 }`.
 * Nunca cae al Tenant: eso solo lo hace la cotización con cambio de plan o frecuencia.
 */
export function readBilledFiscalQuota(bill: Pick<Billplan, 'specificData'> | null | undefined): FiscalQuota | null {
    const count = readSpecificNumber(bill?.specificData, EXTRA_CONTROL_NUMBERS_KEYS.count);
    if (count === null) {
        return null;
    }
    return normalizeFiscalQuota({ quantity: count, monthlyAmountUSD: readSpecificNumber(bill?.specificData, EXTRA_CONTROL_NUMBERS_KEYS.monthlyUSD) ?? 0 });
}

/**
 * Réplica de `mathUtil.toFixed(value, 2)` de api-admin: `decimalAdjust` por desplazamiento
 * de exponente. No es `Number(x.toFixed(2))`: 1,005 da 1,01 (y no 1,00).
 */
function round2(value: number): number {
    const numeric = +value;
    if (Number.isNaN(numeric)) {
        return NaN;
    }
    const [mantissa, exponent] = numeric.toString().split('e');
    const shifted = Math.round(+`${mantissa}e${exponent ? +exponent + 2 : 2}`);
    const [shiftedMantissa, shiftedExponent] = shifted.toString().split('e');
    return +`${shiftedMantissa}e${shiftedExponent ? +shiftedExponent - 2 : -2}`;
}

/**
 * Base USD del cupo en el período (`fiscalQuotaPeriodBaseUsd` de api-admin):
 * `round2(mensual × meses × factor)`, en ese orden. `vitalicia`, sin cupo o cantidad 0 ⇒ 0.
 */
export function fiscalQuotaPeriodBaseUsd(quota: unknown, paymentFrequency: string | null | undefined): number {
    const normalized = normalizeFiscalQuota(quota);
    if (!normalized || normalized.quantity === 0) {
        return 0;
    }
    const frequency = (paymentFrequency ?? '').toString().trim().toLowerCase();
    if (frequency === 'vitalicia') {
        return 0;
    }
    return round2(normalized.monthlyAmountUSD * extraUsersMonthsForFrequency(frequency) * extraUsersFrequencyFactor(frequency));
}

/**
 * Δ de una intención sobre una factura que ya cobra `current` (`computeFiscalQuotaAmountDelta`
 * de api-admin): base del período nueva − actual, IVA 16 % solo con `plus`, sin retención.
 */
export function fiscalQuotaAmountDelta({
    current,
    next,
    paymentFrequency,
    plus,
}: {
    current: FiscalQuota | null;
    next: FiscalQuota | null;
    paymentFrequency: string | null | undefined;
    plus: boolean;
}): { deltaBase: number; deltaPlus: number; deltaAmount: number } {
    const deltaBase = round2(fiscalQuotaPeriodBaseUsd(next, paymentFrequency) - fiscalQuotaPeriodBaseUsd(current, paymentFrequency));
    const deltaPlus = plus ? round2(deltaBase * FISCAL_QUOTA_IVA_RATE) : 0;
    return { deltaBase, deltaPlus, deltaAmount: round2(deltaBase + deltaPlus) };
}

/**
 * Cupo que cobra una cantidad pedida (`resolveFiscalChargeDecision` de api-admin):
 * 0 ⇒ `{ 0, 0 }`; la misma cantidad del Tenant con el plan actual fiscal conserva su precio
 * congelado; cualquier otra toma el `amount` de su tier. Fuera del catálogo ⇒ `null`.
 */
export function resolveRequestedFiscalQuota(
    quantity: number,
    { tenantQuota, currentPlanIsFiscal, tiers }: { tenantQuota: FiscalQuota | null | undefined; currentPlanIsFiscal: boolean; tiers: FiscalOffer['tiers'] | null | undefined },
): FiscalQuota | null {
    if (!Number.isInteger(quantity) || quantity < 0 || quantity > MAX_EXTRA_CONTROL_NUMBERS) {
        return null;
    }
    if (quantity === 0) {
        return { quantity: 0, monthlyAmountUSD: 0 };
    }
    const tenant = normalizeFiscalQuota(tenantQuota);
    if (currentPlanIsFiscal && tenant && tenant.quantity === quantity) {
        return { quantity, monthlyAmountUSD: tenant.monthlyAmountUSD };
    }
    const tier = (tiers ?? []).find(item => item.quantity === quantity);
    return tier ? { quantity, monthlyAmountUSD: tier.amount } : null;
}

export interface FiscalQuotaOption {
    quantity: number;
    monthlyAmountUSD: number;
    label: string;
    /** Cantidad vigente del comercio (factura o Tenant): se conserva aunque el catálogo ya no la tenga (D16). */
    current: boolean;
}

const formatMonthlyUsd = (value: number): string => `$${value.toFixed(2)}/mes`;

/**
 * Opciones del selector, ascendentes, con el copy de signup: «N incluidas» (0), «+q · $x/mes»
 * (tiers) y las cantidades vigentes (`keep`, en orden de prioridad) con su precio y «(actual)».
 * Una cantidad vigente que el catálogo también tiene se pinta con el precio vigente, que es
 * el que se cobra.
 */
export function buildFiscalQuotaOptions({
    tiers,
    includedQuantity,
    keep,
}: {
    tiers: FiscalOffer['tiers'] | null | undefined;
    includedQuantity: number | null | undefined;
    keep: (FiscalQuota | null | undefined)[];
}): FiscalQuotaOption[] {
    const byQuantity = new Map<number, FiscalQuotaOption>();
    byQuantity.set(0, {
        quantity: 0,
        monthlyAmountUSD: 0,
        label: includedQuantity === null || includedQuantity === undefined ? 'Cupo incluido' : `${includedQuantity} incluidas`,
        current: false,
    });
    for (const raw of keep) {
        const quota = normalizeFiscalQuota(raw);
        if (!quota || quota.quantity === 0 || byQuantity.has(quota.quantity)) {
            continue;
        }
        byQuantity.set(quota.quantity, {
            quantity: quota.quantity,
            monthlyAmountUSD: quota.monthlyAmountUSD,
            label: `+${quota.quantity} · ${formatMonthlyUsd(quota.monthlyAmountUSD)} (actual)`,
            current: true,
        });
    }
    for (const tier of tiers ?? []) {
        if (byQuantity.has(tier.quantity)) {
            continue;
        }
        byQuantity.set(tier.quantity, {
            quantity: tier.quantity,
            monthlyAmountUSD: tier.amount,
            label: `+${tier.quantity} · ${formatMonthlyUsd(tier.amount)}`,
            current: false,
        });
    }
    return [...byQuantity.values()].sort((a, b) => a.quantity - b.quantity);
}
